import React, { useState } from "react";

import { useDeleteDag } from "api/api-hooks/airflow-hooks";
import { redirectToAirflowHomeUrl } from "api/ScheduledTasksApi";
import ActionButtons from "components/basic/action-buttons/ActionButtons";
import CollapsibleText from "components/basic/collapsible-text/CollapsibleText";
import DataTable from "components/basic/data-table/DataTable";
import { TableColumn } from "components/pages/measurement-devices/utils/types";
import { useHasPermissions } from "hooks/useHasPermissions";
import airflowLogo from "img/airflow-icon16.png";
import { Button } from "primereact/button";
import { DataTableProps } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Link } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { AirflowDag, AirflowTaskDto } from "types/airflow";
import AirflowDagForm from "./AirflowDagForm";
import AirflowTaskTable from "./AirflowTaskTable";
import SearchUsage from "./SearchUsage";

type AirflowDagListProps = {
  data: AirflowDag[];
  loading?: boolean;
};

const AirflowDagList: React.FC<AirflowDagListProps> = ({ data, loading }) => {
  const deleteDag = useDeleteDag();

  const canEdit = useHasPermissions("AIRFLOW_EDIT");

  const renderActions = (rowData: AirflowDag) => {
    return <ActionButtons onClickDelete={() => deleteDag.mutate(rowData.dagId)} />;
  };

  const renderTaskCount = (rowData: AirflowDag) => <span className="flex">{rowData.tasks.length}</span>;

  const renderLink = (rowData: AirflowDag) => <Link to={`/airflow/${rowData.dagId}`}>{rowData.title}</Link>;

  const renderDagId = (rowData: AirflowDag) => {
    return <CollapsibleText text={rowData.dagId} />;
  };

  const columns: TableColumn<AirflowDag>[] = [
    {
      field: "dagId",
      header: "Airflow ID",
      columnKey: "dagId",
      body: renderDagId,
    },
    {
      field: "title",
      header: T("word.title"),
      body: renderLink,
      columnKey: "title",
    },
    {
      field: "description",
      header: T("word.description"),
      style: { maxWidth: "30rem", wordBreak: "break-word" },
      columnKey: "description",
    },
    {
      field: "cronSchedule",
      header: T("word.schedule"),
      columnKey: "cronSchedule",
    },
    {
      field: "tasks",
      header: T("word.tasks-count"),
      columnKey: "tasksCount",
      style: { justifyItems: "center" },
      body: renderTaskCount,
    },
    {
      header: T("word.actions"),
      columnKey: "actions",
      style: { justifyItems: "center" },
      body: renderActions,
    },
  ];

  const tableConfig: DataTableProps<AirflowDag[]> = {
    rows: 25,
    loading,
    header: <DagsHeader canEdit={canEdit} tasks={data?.map(d => d.tasks).flat()} />,
  };

  return (
    <DataTable
      columns={columns.filter(col => (col.columnKey === "actions" ? canEdit : true))}
      data={data ?? []}
      tableConfig={tableConfig}
    />
  );
};

export default AirflowDagList;

const DagsHeader = ({ canEdit, tasks }: { canEdit?: boolean; tasks?: AirflowTaskDto[] }) => {
  const [createDagDialog, setCreateDagDialog] = useState(false);
  const [searchTemplateDialog, setSearchTemplateDialog] = useState(false);
  const [searchTasksDialog, setSearchTasksDialog] = useState(false);

  return (
    <>
      <div className="flex justify-content-between px-3 py-2 flex-wrap gap-2">
        <div>
          {canEdit && (
            <Button
              label={`${T("action.create")} DAG`}
              icon="pi pi-plus"
              className="dashboard-editor-button icon-green border-green"
              onClick={() => setCreateDagDialog(true)}
            />
          )}
        </div>
        <div className="flex gap-2">
          <Button
            label={T("action.search-for-tasks")}
            icon="pi pi-search"
            className="dashboard-editor-button icon-purple border-purple"
            onClick={() => setSearchTasksDialog(true)}
          />
          <Button
            label={T("action.search-usage")}
            icon="pi pi-search"
            className="dashboard-editor-button icon-purple border-purple"
            onClick={() => setSearchTemplateDialog(true)}
          />
          <a href={redirectToAirflowHomeUrl} target="_blank" rel="noreferrer">
            <Button
              type="button"
              label="Airflow"
              icon={<img src={airflowLogo} alt="" className="mr-2" />}
              className="dashboard-editor-button border-black p-button-outlined"
              severity="contrast"
            />
          </a>
        </div>
      </div>

      <Dialog
        visible={searchTasksDialog}
        header={`${T("action.search-for-tasks")}`}
        onHide={() => setSearchTasksDialog(false)}
        style={{ width: "min(100%, 100rem)", height: "min(100%, 60rem)" }}
      >
        <AirflowTaskTable tasks={tasks ?? []} />
      </Dialog>

      <Dialog
        visible={createDagDialog}
        header={`${T("action.create-new")} DAG`}
        onHide={() => setCreateDagDialog(false)}
        style={{ width: "min(100%, 700px)" }}
      >
        <AirflowDagForm closeDialog={() => setCreateDagDialog(false)} />
      </Dialog>

      <Dialog
        visible={searchTemplateDialog}
        header={T("action.search-usage")}
        onHide={() => setSearchTemplateDialog(false)}
        style={{ width: "min(100%, 700px)" }}
      >
        <SearchUsage closeDialog={() => setSearchTemplateDialog(false)} />
      </Dialog>
    </>
  );
};
